import { DetailsPanel } from "./ui";
import { Component, useEffect, useLayoutEffect, type ErrorInfo, type ReactNode } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
  useNavigate,
} from "react-router";

import { releaseBootOverlay } from "./boot-overlay";
import { Account } from "./routes/account";
import { AiConsentGate, ConsentPage } from "./routes/ai-consent";
import { CreditsHistory } from "./routes/credits-history";
import { DeleteAccount } from "./routes/delete-account";
import { Unsubscribe } from "./routes/unsubscribe";
import { Diagnostics } from "./routes/diagnostics";
import { Enroll } from "./routes/enroll";
import { Home } from "./routes/home";
import { KitchenSink } from "./routes/kitchen-sink";
import { Result } from "./routes/result";
import { Screen } from "./routes/screen";
import { Session } from "./routes/session";
import { Sessions } from "./routes/sessions";
import { Settings } from "./routes/settings";
import { SignIn } from "./routes/signin";
import { Welcome } from "./routes/welcome";
import { legacyResultPath, redirectFor, type Need } from "./routes/guard-policy";
import { t } from "./contract/strings";
import { SignInOutcomeRouter } from "./routes/signin-outcome";
import { isEnrolled, useApp } from "./state/store";
import { rememberReturnTo, resultPathOf, takeReturnTo } from "./state/return-to";
import { captureScreenViewed, startAnalytics } from "./analytics";

/**
 * Routes, and why they are paths rather than fragments.
 *
 * The magic-link flows put their tokens in `location.hash` -- never the query string,
 * because a fragment is the one part of a URL no server, mail scanner or `Referer` header
 * ever sees, and the token is single-use. That rules hash routing out: the router and the
 * sign-in path would be reading the same slot for different purposes.
 */
export const routes = [
  {
    element: <Shell />,
    children: [
      { path: "/", element: <HomeEntry /> },
      // The first-run intro and Welcome (#1095), in front of sign-in on a fresh device's
      // plain launch only (`welcomeDue`); Get started goes on to `/signin`.
      { path: "/welcome", element: <Guard need="signed-out" element={<Welcome />} /> },
      { path: "/signin", element: <Guard need="signed-out" element={<SignIn />} /> },
      // The native apps' sign-in return (#796). Its `?code=` or `#signin=` belongs to
      // the app that holds the PKCE verifier; spending it here would stop the link
      // working there. So a browser that lands here -- no app installed, or the email
      // opened on a computer -- goes to sign-in with neither part of the URL.
      { path: "/app/signin", element: <Navigate to="/signin" replace /> },
      // The AI consent is its own screen in analytics, and this alias is the one address
      // that shows it alone (#1085). The blocking gate still overlays every screen.
      { path: "/consent", element: <Guard need="signed-in" element={<ConsentPage />} /> },
      { path: "/enroll", element: <Guard need="unenrolled" element={<Enroll />} /> },
      { path: "/rerecord", element: <Guard need="enrolled" element={<Enroll rerecord />} /> },
      // No join code in this path, deliberately: the code is the join secret and a URL is
      // in history and over somebody's shoulder. Results are identified by session id,
      // which is not.
      { path: "/session", element: <Guard need="session" element={<Session />} /> },
      { path: "/session/:id/result", element: <Guard need="signed-in" element={<Result />} /> },
      { path: "/sessions", element: <Guard need="signed-in" element={<Sessions />} /> },
      { path: "/settings", element: <Guard need="enrolled" element={<Settings />} /> },
      { path: "/account", element: <Guard need="signed-in" element={<Account />} /> },
      // The plans (#1010): Account with the plans open, where Home's Choose a plan goes.
      { path: "/plans", element: <Guard need="signed-in" element={<Account plans />} /> },
      { path: "/credits", element: <Guard need="signed-in" element={<CreditsHistory />} /> },
      // Deleting an account without the app or a sign-in (#781): unguarded, because the
      // person may have neither here. Its emailed link carries `#delete=`, which the
      // sign-in outcome router never reads.
      { path: "/delete-account", element: <DeleteAccount /> },
      // The emailed meeting email's unsubscribe link lands here (#864): unguarded like the
      // delete page, because there is no sign-in behind it -- the token in the query is the
      // whole grant, and it can only turn that account's meeting emails off. The API's
      // one-click endpoint redirects mailed links to this page.
      { path: "/unsubscribe", element: <Unsubscribe /> },
      ...(import.meta.env.DEV ? [{ path: "/diagnostics", element: <Diagnostics /> }] : []),
      // Dev-only, and absent rather than blocked in a production build: a route that
      // exists and refuses is still a route somebody can find.
      ...(import.meta.env.DEV ? [{ path: "/kitchen-sink", element: <KitchenSink /> }] : []),
      // Signed out, even an unknown address is the front door.
      { path: "*", element: <Guard need="signed-in" element={<NotFound />} /> },
    ],
  },
];

const router = createBrowserRouter(routes);

export function App() {
  return <RouterProvider router={router} />;
}

/**
 * A route that is only reachable from a particular state, and the redirect for when it is
 * not.
 *
 * Nothing redirects until boot knows both halves of who this is. Credentials, template and
 * keypair come from IndexedDB in milliseconds; the account comes from a request run inside
 * the model-download window, and a guard acting between the two answers on stale
 * information. [V] It did: a reload straight onto `/account` was sent to the front door
 * (no account *yet*) and from there to home (account arrived) -- two redirects and two
 * history entries for a screen that was reachable all along. The boot overlay is over all
 * of this anyway, so the wait costs nothing visible.
 */
/**
 * A redirect that keeps the query string.
 *
 * [V] Without this the deploy gate cannot run its most important half. `tools/ort_probe.mjs`
 * asks the built app to expose its ecapa module by loading it with `?probe=1`, and its
 * offline leg then *reloads* to let the service worker take control -- but the first guard
 * redirect (`/` -> `/signin`, before anyone is signed in) dropped the query, so the reload
 * landed on a bare `/signin`, the hook was never exposed, and the probe fell through to
 * importing `/lib/ecapa.js`, which does not exist in a build. The gate that proves a device
 * can still score with no network failed on a URL detail.
 *
 * These redirects are normalisation -- "you asked for a screen you cannot be on, here is the
 * one you can" -- and normalising a path is no reason to discard what came with it. The
 * fragment is deliberately not carried: it holds the magic-link token, `signin.ts` consumes
 * it once and clears it, and re-attaching it to the next URL would put a credential back
 * into history.
 */
function Redirect({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={{ pathname: to, search }} replace />;
}

/**
 * Home, unless the URL is an old notes link (`/?session=<id>`), which goes to the result
 * route before the guard can send it to sign-in or enrolment (#498).
 */
function HomeEntry() {
  const { search } = useLocation();
  const legacy = legacyResultPath(search);
  return legacy ? <Navigate to={legacy} replace /> : <Guard need="enrolled" element={<Home />} />;
}

/** The consent screen on its own address: the same full-screen sheet the gate shows. */
function Guard({ need, element }: { need: Need; element: ReactNode }) {
  /*
   * Selected field by field rather than `useApp()`, which subscribes this component to
   * every store change -- including `elapsedSec` and `peak`, which move twice a second
   * during a session. The guard's answer depends on five things; re-deciding it on the
   * level meter is work with no possible result.
   */
  const identityLoaded = useApp((s) => s.identityLoaded);
  const accountLoaded = useApp((s) => s.accountLoaded);
  const account = useApp((s) => s.account);
  const credentials = useApp((s) => s.credentials);
  const template = useApp((s) => s.template);
  const keypair = useApp((s) => s.keypair);
  const deviceRevoked = useApp((s) => s.deviceRevoked);
  const session = useApp((s) => s.session);
  const welcomeDone = useApp((s) => s.welcomeDone);
  const launchPlain = useApp((s) => s.launchPlain);
  const location = useLocation();

  const loaded = identityLoaded && accountLoaded;
  const to = loaded
    ? redirectFor(need, {
        account,
        credentials,
        template,
        keypair,
        deviceRevoked,
        session,
        welcomeDone,
        launchPlain,
      })
    : null;
  // A meeting's notes opened signed out: kept for after sign-in (`ReturnToRouter`).
  const keep = loaded && !account && to === "/signin" ? resultPathOf(location) : null;
  useEffect(() => {
    if (keep) rememberReturnTo(keep);
  }, [keep]);

  if (!loaded) return null;
  return to === null ? element : <Redirect to={to} />;
}

/**
 * After sign-in -- and the voice setup that follows it on a new device -- go on to the
 * meeting a signed-out visitor was opening (`state/return-to.ts`), as the apps open a held
 * result once the signed-in shell is up. Mounted after `SignInOutcomeRouter` and the
 * screen (whose guard sends a just-signed-in visitor off sign-in), so its effect runs last
 * and its navigation is the one that stands when they all act on the same sign-in.
 */
function ReturnToRouter() {
  const navigate = useNavigate();
  const ready = useApp((s) => s.identityLoaded && s.accountLoaded);
  const signedIn = useApp((s) => Boolean(s.account));
  const enrolled = useApp(isEnrolled);

  useEffect(() => {
    if (!ready || !signedIn || !enrolled) return;
    const back = takeReturnTo();
    if (back) navigate(back, { replace: true });
  }, [ready, signedIn, enrolled, navigate]);

  return null;
}

/**
 * Everything inside `<main>`: the fault banner, then whichever screen is up.
 *
 * The header, footer, theme toggle, boot overlay and update toast are all static in
 * `index.html` and outside this tree. They belong to no screen and survive every change of
 * one -- which is what let the boot overlay cover them all in the first place. The screen
 * only says whether the header and the footer show (`Screen`'s `data-screen` and
 * `data-footer`).
 */
function Shell() {
  // `BootRelease` outside the boundary: a screen that throws on its first render must still
  // bring the overlay down, onto the boundary's fallback, never leave it up for good.
  return (
    <>
      <BootRelease />
      <FaultBoundary>
        <AnalyticsObserver />
        <SignInOutcomeRouter />
        <FaultBanner />
        <Outlet />
        <ReturnToRouter />
        <AiConsentGate />
      </FaultBoundary>
    </>
  );
}

/**
 * The boot overlay comes down here on the signed-in path (#1043), not in `boot.ts`.
 *
 * Every guard renders nothing until both halves of who this is have loaded
 * (`identityLoaded && accountLoaded`). Hiding the overlay from boot, the moment they had,
 * left room for a frame of the header over an empty column before React drew the screen.
 * A layout effect runs after the screen is in the DOM and before it is painted, so the
 * overlay starts leaving in the same frame the screen arrives (fading off it over 0.24 s,
 * #1066; `releaseBootOverlay`). `ready` is boot's own word that it
 * is done; until then (and on its error paths, which never say it) the overlay stays.
 * Signed out, boot still takes it down itself.
 */
function BootRelease() {
  const ready = useApp((s) => s.ready);
  const identityLoaded = useApp((s) => s.identityLoaded);
  const accountLoaded = useApp((s) => s.accountLoaded);
  useLayoutEffect(() => {
    if (!ready || !identityLoaded || !accountLoaded) return;
    releaseBootOverlay();
  }, [ready, identityLoaded, accountLoaded]);
  return null;
}

/** Load the optional SDK after the first paint and report fixed route names only. */
function AnalyticsObserver() {
  const location = useLocation();

  useEffect(() => {
    captureScreenViewed(location.pathname, location.key);
  }, [location.key, location.pathname]);

  useEffect(() => {
    let timer = 0;
    const frame = window.requestAnimationFrame(() => {
      timer = window.setTimeout(() => void startAnalytics(), 0);
    });
    return () => {
      window.cancelAnimationFrame(frame);
      if (timer) window.clearTimeout(timer);
    };
  }, []);

  return null;
}

/**
 * Whatever broke, said out loud.
 *
 * There is no console on a phone. `app.js` wrote this into whichever status line was on
 * screen; with screens that come and go, it gets its own place above them, and the same
 * error styling so it reads as the app talking rather than as chrome.
 */
export function FaultBanner() {
  const faults = useApp((s) => s.faults);
  if (!faults.length) return null;
  return (
    <div className="mt-4 text-note min-h-status-line text-accent font-medium" role="alert">
      <p>{t("app.screen_broke")}</p>
      <a href="/">{t("app.reload")}</a>
      <DetailsPanel summary={t("common.technical_details")}>
        {faults.map((fault) => <p key={fault}>{fault}</p>)}
      </DetailsPanel>
    </div>
  );
}

/**
 * A render that throws takes the whole tree with it, and `window.onerror` never sees it --
 * React catches and rethrows into a boundary, or unmounts everything if there is none.
 * Unmounting everything on a device is a blank page under a header, which reports nothing.
 */
export class FaultBoundary extends Component<{ children: ReactNode }, { message: string | null }> {
  state: { message: string | null } = { message: null };

  /**
   * The message is captured into the boundary's own state, and that is the whole point.
   *
   * [V] Reading it back out of the store instead produced a blank page with an empty
   * `role="alert"` in it. React's order is `getDerivedStateFromError` -> the fallback render
   * -> `componentDidCatch`, and `reportFault` runs in the last of those; the fallback
   * therefore rendered against an empty list, and a class component that does not subscribe
   * never re-rendered to pick anything up. The one screen whose entire job is to say what
   * broke said nothing at all, which is the failure I14 exists to prevent.
   */
  static getDerivedStateFromError(error: Error) {
    return { message: error.message || String(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Still reported to the store, so the banner carries it too once anything re-renders --
    // but nothing rendered below depends on that having happened.
    useApp.getState().reportFault(t("app.screen_broke_fault"), error.message);
    // The component stack names the screen, which the message on its own does not.
    console.error(error, info.componentStack);
  }

  render() {
    if (this.state.message !== null) {
      return (
        <Screen name="home">
          {/* R11: the minimum error is the screen message with Reload beside it; anything
              else is secondary and asked for. */}
          <div className="mt-4 text-note text-accent font-medium" role="alert">
            <p>{t("app.screen_broke")}</p>
            {/* A way out, because the boundary does not re-mount the screen that threw:
                without a route forward this is a dead page with an explanation on it. */}
            <a href="/">{t("app.reload")}</a>
          </div>
          <DetailsPanel summary={t("common.technical_details")}>
            <p>{this.state.message}</p>
            {/* The maintenance reset is advanced, not primary advice: same words and
                action, kept here with the raw error rather than beside Reload. */}
            <p className="mt-4 text-lede text-ink-soft">
              {t("app.reset_hint")}
              <code>{t("app.reset_code")}</code>
              {t("app.reset_tail")}
            </p>
          </DetailsPanel>
        </Screen>
      );
    }
    return this.props.children;
  }
}

function NotFound() {
  return (
    <Screen name="home">
      <h2>{t("app.not_found")}</h2>
      <p className="mt-0 mx-0 mb-4 text-lede text-ink-soft">
        {t("app.not_found_hint")}
        <a href="/">{t("app.back_to_app")}</a>
      </p>
    </Screen>
  );
}
